import { useState } from 'react'
import { Badge, Card, DataState, PageHeader } from '@/components/Common'
import { CheckField, Field, FormSection, TextArea, TextField } from '@/components/Form'
import { ConfirmDialog, Modal } from '@/components/Modal'
import { useAuth } from '@/lib/auth'
import { api, ApiError, type FacilityTree, type Reference, type Source } from '@/lib/api'
import { useFetch } from '@/lib/hooks'
import { useResource } from '@/lib/resource'

const EMPTY = {
  name: '',
  description: '',
  kind: 'local' as 'local' | 'odbc',
  connection_string: '',
  base_query: '',
  read_uncommitted: true,
  query_timeout_seconds: 15,
  max_rows: 500,
  facility_column: '',
  department_column: '',
  enabled: true,
  notes: '',
}

type Draft = typeof EMPTY

/**
 * Where worklist items come from: our own table, or somebody else's database.
 *
 * The connection string is never shown. It holds credentials for a production
 * RIS, and rendering it into a form field would put it in the browser's
 * memory, its autofill and any screenshot of the page — so the API returns a
 * redacted summary and accepts a replacement.
 */
export function Sources() {
  const sources = useResource<Source>('/api/v1/sources')
  const reference = useFetch<Reference>('/api/v1/system/reference')
  const tenancy = useFetch<FacilityTree[]>('/api/v1/tenancy')
  const { can } = useAuth()

  const [editing, setEditing] = useState<Source | 'new' | null>(null)
  const [detail, setDetail] = useState<Source | null>(null)
  const [deleting, setDeleting] = useState<Source | null>(null)

  const mayEdit = can('config_admin')
  const list = sources.data ?? []

  const open = async (source: Source) => {
    // The list omits mappings and aliases; the detail view needs them.
    setDetail(await api.get<Source>(`/api/v1/sources/${source.id}`))
  }

  return (
    <>
      <PageHeader
        title="Sources"
        subtitle="Where scheduled steps are read from."
        actions={
          mayEdit && (
            <button className="btn btn-primary" onClick={() => setEditing('new')}>
              Add source
            </button>
          )
        }
      />

      {sources.saveError && <div className="notice notice-error">{sources.saveError}</div>}

      <Card>
        <p className="card-note">
          A <strong>local</strong> source is the worklist table in this database
          — fed by the order feed or by hand. An <strong>odbc</strong> source is
          somebody else's RIS, read <em>read-only</em> and mapped onto DICOM
          attributes by configuration. Give a foreign source a login with SELECT
          and nothing else: the daemon issues no writes, but the grant is the
          only real guarantee.
        </p>

        <DataState
          loading={sources.loading}
          error={sources.error}
          empty={list.length === 0}
          emptyMessage="No sources. Add a local one to start entering steps by hand or via the feed."
        />

        {list.length > 0 && (
          <div className="table-wrap">
            <table className="ae-table">
              <thead>
                <tr>
                  <th>Source</th>
                  <th>Kind</th>
                  <th>Points at</th>
                  <th>Worklists</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.map((source) => (
                  <tr key={source.id} className={source.enabled ? undefined : 'row-muted'}>
                    <td>
                      <strong>{source.name}</strong>
                      {!source.enabled && <> <Badge state="DISABLED" /></>}
                      {source.description && <div className="hint">{source.description}</div>}
                    </td>
                    <td>{source.kind}</td>
                    <td className="mono hint">
                      {source.kind === 'local' ? (
                        'worklist_item'
                      ) : source.connection.configured ? (
                        <>
                          {source.connection.server}/{source.connection.database}
                          <div>
                            as {source.connection.username || '—'}
                            {!source.connection.password_set && (
                              <span className="warn-text"> · no password set</span>
                            )}
                          </div>
                        </>
                      ) : (
                        <span className="warn-text">no connection string</span>
                      )}
                    </td>
                    <td className="num">{source.worklist_count}</td>
                    <td className="actions">
                      <button className="btn btn-small" onClick={() => void open(source)}>
                        Details
                      </button>
                      {mayEdit && (
                        <>
                          <button className="btn btn-small" onClick={() => setEditing(source)}>
                            Edit
                          </button>
                          <button
                            className="btn btn-small btn-danger-quiet"
                            onClick={() => setDeleting(source)}
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editing && (
        <SourceEditor
          source={editing === 'new' ? null : editing}
          saving={sources.saving}
          error={sources.saveError}
          onClose={() => {
            setEditing(null)
            sources.clearError()
          }}
          onSave={async (draft) => {
            const saved =
              editing === 'new'
                ? await sources.create(draft)
                : await sources.update(editing.id, draft)
            if (saved) setEditing(null)
          }}
        />
      )}

      {detail && (
        <SourceDetail
          source={detail}
          reference={reference.data ?? null}
          tenancy={tenancy.data ?? []}
          mayEdit={mayEdit}
          onReload={async () => setDetail(await api.get<Source>(`/api/v1/sources/${detail.id}`))}
          onClose={() => {
            setDetail(null)
            sources.reload()
          }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title={`Delete source '${deleting.name}'?`}
          confirmLabel="Delete source"
          destructive
          busy={sources.saving}
          error={sources.saveError}
          onCancel={() => setDeleting(null)}
          onConfirm={async () => {
            if (await sources.remove(deleting.id)) setDeleting(null)
          }}
        >
          <p>Its column mappings and tenancy aliases go with it.</p>
          {deleting.worklist_count > 0 && (
            <div className="notice notice-error">
              {deleting.worklist_count} worklist(s) read from this source. The
              delete will be refused.
            </div>
          )}
        </ConfirmDialog>
      )}
    </>
  )
}

function SourceDetail({
  source,
  reference,
  tenancy,
  mayEdit,
  onReload,
  onClose,
}: {
  source: Source
  reference: Reference | null
  tenancy: FacilityTree[]
  mayEdit: boolean
  onReload: () => Promise<void>
  onClose: () => void
}) {
  const [keyword, setKeyword] = useState('')
  const [column, setColumn] = useState('')
  const [constant, setConstant] = useState('')
  const [transform, setTransform] = useState('none')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const [aliasFacility, setAliasFacility] = useState<number | ''>('')
  const [aliasCode, setAliasCode] = useState('')

  const run = async (work: () => Promise<unknown>) => {
    setBusy(true)
    setError(null)
    try {
      await work()
      await onReload()
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      title={source.name}
      subtitle={source.kind === 'local' ? 'The worklist table in this database' : 'A foreign database, read-only'}
      onClose={onClose}
      footer={
        <button className="btn btn-primary" onClick={onClose}>
          Done
        </button>
      }
    >
      {error && <div className="notice notice-error">{error}</div>}

      {(source.readiness ?? []).map((problem, i) => (
        <div
          key={i}
          className={problem.level === 'error' ? 'notice notice-error' : 'notice notice-warn'}
        >
          {problem.message}
        </div>
      ))}

      {source.kind === 'local' && (
        <p className="card-note">
          No mapping needed — the schema is ours. Scheduled steps arrive through
          the order feed or the Scheduled steps page.
        </p>
      )}

      {source.kind === 'odbc' && (
        <>
          <FormSection title="Base query">
            <pre className="mono">{source.base_query || '(none)'}</pre>
            <p className="hint">
              Treated as a view definition and wrapped:{' '}
              <code>SELECT TOP (n) … FROM ( base ) AS src WHERE …</code>. Every
              value in those predicates is bound and every column name is
              validated and quoted, so nothing a modality sends reaches the text
              of a statement. <strong>One row per scheduled procedure step</strong>{' '}
              — not per order.
            </p>
          </FormSection>

          <FormSection title={`Column mappings (${source.mappings?.length ?? 0})`}>
            <table className="ae-table">
              <thead>
                <tr>
                  <th>DICOM attribute</th>
                  <th>From</th>
                  <th>Transform</th>
                  <th>Filterable</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(source.mappings ?? []).map((mapping) => (
                  <tr key={mapping.id} className={mapping.resolvable ? undefined : 'row-error'}>
                    <td className="mono">
                      {mapping.dicom_keyword}
                      {mapping.in_sps && <span className="hint"> · in step</span>}
                      {!mapping.resolvable && (
                        <div className="warn-text">
                          not a known attribute — never returned
                        </div>
                      )}
                    </td>
                    <td className="mono">
                      {mapping.source_column || (
                        <span className="tag">= {mapping.constant_value}</span>
                      )}
                    </td>
                    <td className="hint">{mapping.transform}</td>
                    <td>{mapping.filterable ? 'yes' : <span className="hint">in memory</span>}</td>
                    <td className="actions">
                      {mayEdit && (
                        <button
                          className="btn btn-small btn-danger-quiet"
                          disabled={busy}
                          onClick={() =>
                            void run(() => api.delete(`/api/v1/mappings/${mapping.id}`))
                          }
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {mayEdit && (
              <>
                <div className="subhead">Add a mapping</div>
                <div className="form-grid">
                  <Field label="DICOM attribute">
                    <select value={keyword} onChange={(e) => setKeyword(e.target.value)}>
                      <option value="">choose…</option>
                      {(reference?.attributes ?? []).map((a) => (
                        <option key={a.keyword} value={a.keyword}>
                          {a.keyword} {a.tag}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <TextField
                    label="Source column"
                    value={column}
                    onChange={(v) => {
                      setColumn(v)
                      if (v) setConstant('')
                    }}
                    hint="A column of the base query."
                  />
                  <TextField
                    label="…or a constant"
                    value={constant}
                    onChange={(v) => {
                      setConstant(v)
                      if (v) setColumn('')
                    }}
                    hint="For a tag the source has no column for — an echo feed's Modality is always US."
                  />
                  <Field label="Transform">
                    <select value={transform} onChange={(e) => setTransform(e.target.value)}>
                      {Object.entries(reference?.transforms ?? {}).map(([key, description]) => (
                        <option key={key} value={key}>
                          {key} — {description}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>
                <div className="form-actions">
                  <button
                    className="btn btn-primary"
                    disabled={busy || !keyword || (!column && !constant)}
                    onClick={() =>
                      void run(async () => {
                        await api.post('/api/v1/mappings', {
                          source_id: source.id,
                          dicom_keyword: keyword,
                          source_column: column,
                          constant_value: constant,
                          transform,
                          filterable: true,
                        })
                        setKeyword('')
                        setColumn('')
                        setConstant('')
                        setTransform('none')
                      })
                    }
                  >
                    Add mapping
                  </button>
                </div>
              </>
            )}
          </FormSection>

          <FormSection
            title="Facility aliases"
            description={
              <>
                How this source spells our facilities. Without one, a query
                scoped to a facility matches <strong>nothing</strong> against
                this source. Several codes may point at the same facility — a
                RIS that absorbed another system emits both.
              </>
            }
          >
            <table className="ae-table">
              <thead>
                <tr>
                  <th>Our facility</th>
                  <th>Their code</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(source.facility_aliases ?? []).map((alias) => (
                  <tr key={alias.id}>
                    <td>{alias.facility_code}</td>
                    <td className="mono">{alias.external_code}</td>
                    <td className="actions">
                      {mayEdit && (
                        <button
                          className="btn btn-small btn-danger-quiet"
                          disabled={busy}
                          onClick={() =>
                            void run(() => api.delete(`/api/v1/facility-aliases/${alias.id}`))
                          }
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {mayEdit && (
              <>
                <div className="form-grid">
                  <Field label="Our facility">
                    <select
                      value={aliasFacility}
                      onChange={(e) =>
                        setAliasFacility(e.target.value === '' ? '' : Number(e.target.value))
                      }
                    >
                      <option value="">choose…</option>
                      {tenancy.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.code} — {f.name}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <TextField
                    label="Their code"
                    value={aliasCode}
                    onChange={setAliasCode}
                    hint={`As it appears in the ${source.facility_column || 'facility'} column.`}
                  />
                </div>
                <div className="form-actions">
                  <button
                    className="btn btn-primary"
                    disabled={busy || aliasFacility === '' || !aliasCode}
                    onClick={() =>
                      void run(async () => {
                        await api.post(`/api/v1/sources/${source.id}/facility-aliases`, {
                          source_id: source.id,
                          facility_id: aliasFacility,
                          external_code: aliasCode,
                        })
                        setAliasCode('')
                      })
                    }
                  >
                    Add alias
                  </button>
                </div>
              </>
            )}
          </FormSection>
        </>
      )}
    </Modal>
  )
}

function SourceEditor({
  source,
  saving,
  error,
  onClose,
  onSave,
}: {
  source: Source | null
  saving: boolean
  error: string | null
  onClose: () => void
  onSave: (draft: Draft) => void
}) {
  const [draft, setDraft] = useState<Draft>(() =>
    source
      ? {
          name: source.name,
          description: source.description,
          kind: source.kind,
          // Never populated from the server — it is not returned. Empty on a
          // save means "leave it alone".
          connection_string: '',
          base_query: source.base_query,
          read_uncommitted: source.read_uncommitted,
          query_timeout_seconds: source.query_timeout_seconds,
          max_rows: source.max_rows,
          facility_column: source.facility_column,
          department_column: source.department_column,
          enabled: source.enabled,
          notes: source.notes,
        }
      : EMPTY,
  )

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }))

  return (
    <Modal
      title={source ? `Edit '${source.name}'` : 'Add source'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={saving || !draft.name}
            onClick={() => onSave(draft)}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && <div className="notice notice-error">{error}</div>}

      <FormSection title="Identity">
        <div className="form-grid">
          <TextField label="Name" value={draft.name} onChange={(v) => set('name', v)} required />
          <Field label="Kind">
            <select
              value={draft.kind}
              onChange={(e) => set('kind', e.target.value as 'local' | 'odbc')}
              disabled={source !== null}
            >
              <option value="local">local — the worklist table in this database</option>
              <option value="odbc">odbc — a foreign database</option>
            </select>
            <span className="field-hint">
              {source ? 'Cannot be changed after creation.' : ''}
            </span>
          </Field>
        </div>
        <TextField
          label="Description"
          value={draft.description}
          onChange={(v) => set('description', v)}
          maxLength={400}
        />
        <CheckField label="Enabled" checked={draft.enabled} onChange={(v) => set('enabled', v)} />
      </FormSection>

      {draft.kind === 'odbc' && (
        <>
          <FormSection
            title="Connection"
            description={
              <>
                <strong>Never shown back.</strong> It holds another system's
                credentials. Leave it blank to keep the current one; type a new
                one to replace it entirely.
              </>
            }
          >
            {source?.connection.configured && (
              <div className="notice notice-info">
                Currently: {source.connection.server}/{source.connection.database} as{' '}
                {source.connection.username || '—'}
                {source.connection.password_set ? ' (password set)' : ' (NO PASSWORD)'}
              </div>
            )}
            <TextField
              label="ODBC connection string"
              value={draft.connection_string}
              onChange={(v) => set('connection_string', v)}
              maxLength={1000}
              placeholder="Driver=/usr/lib/…/libtdsodbc.so;Server=10.1.240.5;Port=1433;Database=RIS;UID=reader;PWD=…;"
              hint="The login behind it should have SELECT on the objects in the base query and nothing else."
            />
          </FormSection>

          <FormSection
            title="Query"
            description="One row per SCHEDULED PROCEDURE STEP — not per order and not per patient. An order with two steps is two rows, or the technologist sees one entry where there should be two."
          >
            <TextArea
              label="Base query"
              value={draft.base_query}
              onChange={(v) => set('base_query', v)}
              rows={6}
              maxLength={4000}
              hint="A SELECT. It is wrapped, not executed bare."
            />
            <div className="form-grid">
              <TextField
                label="Facility column"
                value={draft.facility_column}
                onChange={(v) => set('facility_column', v)}
                hint="The column carrying THEIR site code. Without it, any facility-scoped query against this source is refused — an unenforceable tenancy bound is not a bound."
              />
              <TextField
                label="Department column"
                value={draft.department_column}
                onChange={(v) => set('department_column', v)}
              />
              <Field label="Query timeout (s)">
                <input
                  type="number"
                  value={draft.query_timeout_seconds}
                  onChange={(e) => set('query_timeout_seconds', Number(e.target.value))}
                />
              </Field>
              <Field label="Maximum rows">
                <input
                  type="number"
                  value={draft.max_rows}
                  onChange={(e) => set('max_rows', Number(e.target.value))}
                />
              </Field>
            </div>
            <CheckField
              label="Read uncommitted"
              detail="On by default for a foreign source, and it is the polite setting: a RIS is a live transactional system and a modality polling every thirty seconds must never hold a lock its order-entry screen waits behind. Turn it off only for a reporting replica."
              checked={draft.read_uncommitted}
              onChange={(v) => set('read_uncommitted', v)}
            />
          </FormSection>
        </>
      )}

      <FormSection title="Notes">
        <TextArea
          label="Notes"
          value={draft.notes}
          onChange={(v) => set('notes', v)}
          maxLength={1000}
        />
      </FormSection>
    </Modal>
  )
}
